// src/pages/LoginPage.tsx — Pantalla de login POW-02 (Admin)
// Sigue el prototipo admin.html: Step1 credentials → Step2 OTP 6 dígitos → Forgot password flow
import { useEffect, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Mail, ArrowLeft, Eye, EyeOff } from 'lucide-react'
import { useAuthStore } from '@/store/authStore'
import api from '@/services/api'
import logoLight from '@/assets/logo-light.png'
import clsx from 'clsx'
import toast from 'react-hot-toast'
import { distribuirOtpPegado, OTP_LENGTH } from '@/utils/otp'
import { destinoInterno, normalizeApiDetail } from '@/utils/authGuards'

export default function LoginPage() {
  const navigate = useNavigate()
  const {
    step,
    loading,
    error,
    login,
    verify2FA,
    resend2FA,
    clearError,
    resetToCredentials,
    codigoInvalidado,
    intentosRestantes,
    cuentaBloqueadaMinutos,
  } = useAuthStore()

  // Prefill de desarrollo externalizado (LOGIN-20): define VITE_DEV_LOGIN_EMAIL / VITE_DEV_LOGIN_PASSWORD.
  const [correo, setCorreo] = useState(import.meta.env.DEV ? (import.meta.env.VITE_DEV_LOGIN_EMAIL ?? '') : '')
  const [contrasena, setContrasena] = useState(import.meta.env.DEV ? (import.meta.env.VITE_DEV_LOGIN_PASSWORD ?? '') : '')
  const [showPass, setShowPass] = useState(false)
  const [otp, setOtp] = useState(['', '', '', '', '', ''])
  const [countdown, setCountdown] = useState(600) // 10 min
  const [resendCooldown, setResendCooldown] = useState(60)
  const [resending, setResending] = useState(false)
  // Temporizador del bloqueo de cuenta (LOGIN-11): segundos restantes.
  const [bloqueoSeg, setBloqueoSeg] = useState(0)
  const otpRefs = useRef<(HTMLInputElement | null)[]>([])
  // Guard sincrónico: evita doble POST a verify-2fa entre auto-verify y botón (BUG-06).
  const verifyingRef = useRef(false)

  // ── Forgot password state ──────────────────────────────────────
  const [showForgot, setShowForgot] = useState(false)
  const [forgotEmail, setForgotEmail] = useState('')
  const [forgotLoading, setForgotLoading] = useState(false)
  const [forgotError, setForgotError] = useState<string | null>(null)
  const [forgotSent, setForgotSent] = useState(false)

  // Redirigir si ya autenticado (BUG-08: respeta `returnTo` interno)
  useEffect(() => {
    const hasToken = Boolean(localStorage.getItem('pow_access_token'))
    if (step === 'authenticated') {
      if (hasToken) {
        const rt = new URLSearchParams(window.location.search).get('returnTo')
        navigate(destinoInterno(rt, '/dashboard'), { replace: true })
      } else {
        useAuthStore.getState().logout()
      }
    }
  }, [step, navigate])

  // Countdown OTP (10 min)
  useEffect(() => {
    if (step !== '2fa') {
      setCountdown(600)
      return
    }
    const t = setInterval(() => setCountdown((p) => Math.max(0, p - 1)), 1000)
    return () => clearInterval(t)
  }, [step])

  // Cooldown de reenvío (60s) — POW-02 §4
  useEffect(() => {
    if (step !== '2fa') {
      setResendCooldown(60)
      return
    }
    const t = setInterval(() => setResendCooldown((p) => Math.max(0, p - 1)), 1000)
    return () => clearInterval(t)
  }, [step])

  // Bloqueo de cuenta (LOGIN-11): sincroniza desde el store y decrementa.
  useEffect(() => {
    setBloqueoSeg((cuentaBloqueadaMinutos ?? 0) * 60)
  }, [cuentaBloqueadaMinutos])

  useEffect(() => {
    if (bloqueoSeg <= 0) return
    const t = setInterval(() => setBloqueoSeg((p) => Math.max(0, p - 1)), 1000)
    return () => clearInterval(t)
  }, [bloqueoSeg > 0])

  // Reinicio automático al paso 1 si se invalida el código por 3 intentos
  useEffect(() => {
    if (codigoInvalidado) {
      const t = setTimeout(() => {
        setOtp(['', '', '', '', '', ''])
        resetToCredentials()
      }, 2500)
      return () => clearTimeout(t)
    }
  }, [codigoInvalidado, resetToCredentials])

  const fmtCountdown = `${String(Math.floor(countdown / 60)).padStart(2, '0')}:${String(countdown % 60).padStart(2, '0')}`

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault()
    if (bloqueoSeg > 0) return // no reintentar mientras la cuenta está bloqueada (LOGIN-11)
    clearError()
    await login(correo, contrasena)
  }

  const handleResend = async () => {
    if (resendCooldown > 0 || resending) return
    setResending(true)
    clearError()
    const ok = await resend2FA()
    setResending(false)
    if (ok) {
      toast.success('Nuevo código enviado a tu correo.')
      setResendCooldown(60)
      setCountdown(600)
      setOtp(['', '', '', '', '', ''])
      otpRefs.current[0]?.focus()
    }
  }

  // Único punto de disparo de la verificación (BUG-06): el guard es un ref
  // sincrónico, así que un paste + clic simultáneos no envían dos POST a verify-2fa.
  const intentarVerificar = async (code: string) => {
    if (verifyingRef.current || loading || codigoInvalidado) return
    if (code.length !== OTP_LENGTH) return
    verifyingRef.current = true
    try {
      await verify2FA(code)
    } finally {
      verifyingRef.current = false
    }
  }

  const handleOtpChange = (i: number, val: string) => {
    const digits = val.replace(/\D/g, '')
    if (digits.length === 0) {
      const next = [...otp]
      next[i] = ''
      setOtp(next)
      return
    }
    // Un dígito o varios: aplicarOtp auto-verifica si el OTP quedó completo y
    // enfoca la siguiente caja vacía (auto-verify simétrico, LOGIN-12).
    aplicarOtp(distribuirOtpPegado(digits, otp, i))
  }

  // Aplica un nuevo OTP: enfoca la siguiente caja vacía y auto-verifica si quedó completo.
  const aplicarOtp = (next: string[]) => {
    setOtp(next)
    const code = next.join('')
    const nextEmpty = next.findIndex((d) => !d)
    if (nextEmpty >= 0) otpRefs.current[nextEmpty]?.focus()
    else otpRefs.current[OTP_LENGTH - 1]?.focus()
    if (code.length === OTP_LENGTH) void intentarVerificar(code)
  }

  const handleOtpPaste = (i: number, e: React.ClipboardEvent<HTMLInputElement>) => {
    if (codigoInvalidado) return
    const texto = e.clipboardData.getData('text')
    if (!texto) return
    e.preventDefault()
    aplicarOtp(distribuirOtpPegado(texto, otp, i))
  }

  const handleOtpKey = (i: number, e: React.KeyboardEvent) => {
    if (e.key === 'Backspace' && !otp[i] && i > 0) {
      otpRefs.current[i - 1]?.focus()
    }
  }

  const handleVerify = async () => {
    await intentarVerificar(otp.join(''))
  }

  // ── Forgot password handler ────────────────────────────────────
  const handleForgotSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!forgotEmail.trim()) return
    setForgotLoading(true)
    setForgotError(null)
    try {
      const res = await api.post('/api/auth/contrasena/reset', { correo: forgotEmail, origen: 'pow' })
      setForgotSent(true)
      setForgotEmail('')
      toast.success(
        res.data?.mensaje || 'Si el correo existe, recibirás un enlace de recuperación.',
        { duration: 6000 }
      )
    } catch (err: any) {
      setForgotError(
        normalizeApiDetail(err.response?.data?.detail, 'Ocurrió un error al enviar el enlace. Intenta de nuevo.')
      )
    } finally {
      setForgotLoading(false)
    }
  }

  const handleBackToLogin = () => {
    setShowForgot(false)
    setForgotEmail('')
    setForgotError(null)
    setForgotSent(false)
  }

  return (
    <div className="min-h-screen flex items-center justify-center relative overflow-hidden"
      style={{ background: '#FFFFFF' }}>

      {/* Orbs decorativos */}
      <div className="absolute rounded-full animate-pulse"
        style={{
          width: '500px', height: '500px',
          background: '#0050F0', opacity: 0.12,
          top: '-150px', left: '-100px',
          filter: 'blur(100px)'
        }} />
      <div className="absolute rounded-full animate-pulse"
        style={{
          width: '400px', height: '400px',
          background: '#00DCA0', opacity: 0.10,
          bottom: '-100px', right: '-80px',
          filter: 'blur(100px)',
          animationDelay: '1s'
        }} />

      {/* Card */}
      <div className="relative z-10 w-full max-w-[420px] mx-4 fade-in">
        <div className="card" style={{ padding: '40px', background: '#FFFFFF', boxShadow: '0 4px 6px -1px rgba(0,0,0,0.08), 0 2px 4px -2px rgba(0,0,0,0.05)', borderRadius: '16px', border: '1px solid rgba(0,0,0,0.06)' }}>

          {/* Logo */}
          <div className="flex flex-col items-center mb-[28px]">
            <img src={logoLight} alt="MIVA" className="h-[200px] mb-[10px] object-contain" />
            <h1 className="text-[1.5rem] font-[800] text-center mb-[4px]" style={{ color: '#071B3F' }}>
              {showForgot ? 'Recuperar contraseña' : 'Panel Administrador'}
            </h1>
            <p className="text-center text-[0.85rem]" style={{ color: '#5E6E89' }}>
              {showForgot
                ? 'Ingresa tu correo para recibir el enlace'
                : <>Acceso exclusivo — <span className="inline-flex items-center rounded-full font-[600] tracking-[0.02em]" style={{ background: 'rgba(59,130,246,0.12)', color: '#3B82F6', padding: '3px 10px', fontSize: '0.72rem' }}>SaaS</span></>
              }
            </p>
          </div>

          {/* Error global (login / 2fa) */}
          {error && !showForgot && (
            <div className="mb-4 p-3 rounded-lg text-sm"
              style={{ background: 'rgba(239,68,68,0.12)', border: '1px solid rgba(239,68,68,0.3)', color: '#EF4444' }}>
              {error}
            </div>
          )}

          {/* ── STEP: Forgot Password ── */}
          {showForgot && (
            <div className="fade-in">

              {/* Ícono ilustrativo */}
              <div className="flex justify-center mb-[20px]">
                <div className="flex items-center justify-center rounded-[20px]"
                  style={{ width: 64, height: 64, background: 'rgba(0,80,240,0.08)', border: '1.5px solid rgba(0,80,240,0.15)' }}>
                  <Mail size={28} style={{ color: '#0050F0' }} />
                </div>
              </div>

              {!forgotSent ? (
                <form onSubmit={handleForgotSubmit} className="space-y-[14px]">
                  <p className="text-[0.84rem] text-center leading-[1.6] mb-[6px]" style={{ color: '#5E6E89' }}>
                    Te enviaremos un enlace seguro para crear una nueva contraseña de administrador.
                  </p>

                  {/* Error forgot */}
                  {forgotError && (
                    <div className="p-3 rounded-lg text-sm"
                      style={{ background: 'rgba(239,68,68,0.08)', border: '1px solid rgba(239,68,68,0.25)', color: '#EF4444' }}>
                      {forgotError}
                    </div>
                  )}

                  <div className="flex flex-col gap-[6px]">
                    <label className="text-[0.8rem] font-[600] tracking-[0.01em]" style={{ color: '#5E6E89' }}>
                      Correo electrónico
                    </label>
                    <div className="relative flex items-center">
                      <Mail size={15} className="absolute left-[14px]" style={{ color: '#94A3B8', pointerEvents: 'none' }} />
                      <input
                        id="forgot-email-input"
                        className="w-full rounded-[12px] pl-[38px] pr-[14px] py-[10px] text-[0.9rem] outline-none transition-shadow"
                        style={{ background: '#F5F7FA', border: '1.5px solid #CBD5E1', color: '#071B3F' }}
                        type="email"
                        placeholder="correo@empresa.com"
                        autoComplete="email"
                        required
                        value={forgotEmail}
                        onChange={(e) => setForgotEmail(e.target.value)}
                        onFocus={(e) => e.target.style.boxShadow = '0 0 0 3px rgba(0,80,240,0.15)'}
                        onBlur={(e) => e.target.style.boxShadow = 'none'}
                      />
                    </div>
                  </div>

                  <button
                    id="forgot-submit-btn"
                    type="submit"
                    disabled={forgotLoading || !forgotEmail.trim()}
                    className={clsx(
                      'btn btn-primary btn-block mt-[8px]',
                      (forgotLoading || !forgotEmail.trim()) && 'opacity-60 cursor-not-allowed'
                    )}
                  >
                    {forgotLoading ? 'Enviando enlace...' : 'Enviar enlace de recuperación'}
                  </button>
                </form>
              ) : (
                /* ── Estado: enlace enviado ── */
                <div className="text-center space-y-[12px]">
                  <div className="flex justify-center">
                    <div className="flex items-center justify-center rounded-full"
                      style={{ width: 52, height: 52, background: 'rgba(16,185,129,0.10)', border: '1.5px solid rgba(16,185,129,0.25)' }}>
                      <span style={{ fontSize: 24 }}>✅</span>
                    </div>
                  </div>
                  <p className="text-[0.9rem] font-[600]" style={{ color: '#064E3B' }}>
                    Enlace enviado
                  </p>
                  <p className="text-[0.82rem] leading-[1.65]" style={{ color: '#5E6E89' }}>
                    Si el correo está registrado como administrador, recibirás el enlace en los próximos minutos. Revisa también tu carpeta de spam.
                  </p>
                </div>
              )}

              {/* Volver al login */}
              <div className="text-center mt-[20px] pt-[16px]" style={{ borderTop: '1px solid #F1F5F9' }}>
                <button
                  type="button"
                  id="back-to-login-btn"
                  onClick={handleBackToLogin}
                  className="inline-flex items-center gap-[6px] text-[0.82rem] font-[500] transition-colors hover:underline bg-transparent border-none cursor-pointer"
                  style={{ color: '#5E6E89' }}
                >
                  <ArrowLeft size={14} /> Volver al inicio de sesión
                </button>
              </div>
            </div>
          )}

          {/* ── Redirigiendo a Dashboard ── */}
          {!showForgot && step === 'authenticated' && Boolean(localStorage.getItem('pow_access_token')) && (
            <div className="flex flex-col items-center justify-center py-8">
              <div className="w-8 h-8 rounded-full border-2 border-t-transparent animate-spin mb-3"
                   style={{ borderColor: 'var(--color-miva-blue, #0050F0)', borderTopColor: 'transparent' }} />
              <p className="text-xs font-medium" style={{ color: '#5E6E89' }}>Ingresando al panel...</p>
            </div>
          )}

          {/* ── STEP 1: Credentials ── */}
          {!showForgot && (step === 'credentials' || (step === 'authenticated' && !localStorage.getItem('pow_access_token'))) && (
            <form onSubmit={handleLogin} className="space-y-[14px]">
              <div className="flex flex-col gap-[6px]">
                <label className="text-[0.8rem] font-[600] tracking-[0.01em]" style={{ color: '#5E6E89' }}>Correo electrónico</label>
                <div className="relative">
                  <input
                    id="email-input"
                    className="w-full rounded-[12px] px-[14px] py-[10px] text-[0.9rem] outline-none transition-shadow"
                    style={{ background: '#F5F7FA', border: '1.5px solid #CBD5E1', color: '#071B3F' }}
                    type="email"
                    placeholder=""
                    autoComplete="off"
                    required
                    value={correo}
                    onChange={(e) => setCorreo(e.target.value)}
                    onFocus={(e) => e.target.style.boxShadow = '0 0 0 3px rgba(0,80,240,0.15)'}
                    onBlur={(e) => e.target.style.boxShadow = 'none'}
                  />
                </div>
              </div>

              <div className="flex flex-col gap-[6px]">
                <label className="text-[0.8rem] font-[600] tracking-[0.01em]" style={{ color: '#5E6E89' }}>Contraseña</label>
                <div className="relative flex items-center">
                  <input
                    id="pass-input"
                    className="w-full rounded-[12px] pl-[14px] pr-[42px] py-[10px] text-[0.9rem] outline-none transition-shadow"
                    style={{ background: '#F5F7FA', border: '1.5px solid #CBD5E1', color: '#071B3F' }}
                    type={showPass ? 'text' : 'password'}
                    placeholder=""
                    autoComplete="new-password"
                    required
                    value={contrasena}
                    onChange={(e) => setContrasena(e.target.value)}
                    onFocus={(e) => e.target.style.boxShadow = '0 0 0 3px rgba(0,80,240,0.15)'}
                    onBlur={(e) => e.target.style.boxShadow = 'none'}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPass(!showPass)}
                    className="absolute right-[14px] flex items-center justify-center text-slate-400 hover:text-slate-600 focus:outline-none"
                    style={{ color: '#94A3B8', border: 'none', background: 'none', cursor: 'pointer' }}
                  >
                    {showPass ? <EyeOff size={18} /> : <Eye size={18} />}
                  </button>
                </div>
              </div>

              <button id="login-btn" type="submit" disabled={loading || bloqueoSeg > 0}
                className={clsx('btn btn-primary btn-block mt-[20px]', (loading || bloqueoSeg > 0) && 'opacity-60 cursor-not-allowed')}>
                {loading ? 'Verificando...' : bloqueoSeg > 0 ? `Cuenta bloqueada (${Math.ceil(bloqueoSeg / 60)} min)` : 'Ingresar al panel'}
              </button>

              <div className="text-center mt-[16px]">
                <button
                  type="button"
                  id="forgot-password-btn"
                  onClick={() => { clearError(); setShowForgot(true) }}
                  className="text-[0.82rem] font-[500] hover:underline bg-transparent border-none cursor-pointer"
                  style={{ color: '#0050F0' }}
                >
                  ¿Olvidaste tu contraseña?
                </button>
              </div>
            </form>
          )}

          {/* ── STEP 2: OTP 2FA ── */}
          {!showForgot && step === '2fa' && (
            <div className="fade-in">
              <div className="text-center mb-[24px]">
                <div className="flex justify-center mb-[12px]">
                  <Mail size={36} style={{ color: '#0050F0' }} />
                </div>
                <p className="text-[0.9rem] leading-[1.5]" style={{ color: '#5E6E89' }}>
                  Hemos enviado un código de 6 dígitos a <br />
                  <strong style={{ color: '#071B3F' }}>
                    {correo.replace(/(.{2}).*@/, '$1***@')}
                  </strong>
                </p>
                <small className="text-[0.78rem]" style={{ color: '#94A3B8' }}>
                  Válido por <span>{fmtCountdown}</span>
                </small>

                {/* Intentos restantes o alerta de invalidación */}
                {intentosRestantes !== null && !codigoInvalidado && (
                  <div className="mt-2 text-[0.8rem] font-semibold text-amber-600 bg-amber-50 py-1 px-3 rounded-full inline-block">
                    ⚠️ {intentosRestantes} intento{intentosRestantes === 1 ? '' : 's'} restante{intentosRestantes === 1 ? '' : 's'} de 3
                  </div>
                )}
                {codigoInvalidado && (
                  <div className="mt-2 text-[0.8rem] font-bold text-rose-700 bg-rose-50 py-1.5 px-3 rounded-lg border border-rose-200">
                    🚨 Código invalidado. Reiniciando proceso...
                  </div>
                )}
              </div>

              {/* OTP digits */}
              <div className="flex gap-[8px] justify-center mb-[8px]">
                {otp.map((digit, i) => (
                  <input
                    key={i}
                    ref={(el) => { otpRefs.current[i] = el }}
                    className="w-[48px] h-[56px] rounded-[12px] text-center text-[1.4rem] font-[700] outline-none transition-shadow"
                    style={{ background: '#F5F7FA', border: '1.5px solid #CBD5E1', color: '#071B3F' }}
                    type="text"
                    inputMode="numeric"
                    maxLength={1}
                    value={digit}
                    disabled={codigoInvalidado}
                    onChange={(e) => handleOtpChange(i, e.target.value)}
                    onPaste={(e) => handleOtpPaste(i, e)}
                    onKeyDown={(e) => handleOtpKey(i, e)}
                    onFocus={(e) => e.target.style.boxShadow = '0 0 0 3px rgba(0,80,240,0.15)'}
                    onBlur={(e) => e.target.style.boxShadow = 'none'}
                    id={`otp-${i}`}
                  />
                ))}
              </div>

              <button id="verify-btn" onClick={handleVerify} disabled={loading || otp.join('').length < 6 || codigoInvalidado}
                className={clsx('btn btn-primary btn-block mt-[20px]', (loading || otp.join('').length < 6 || codigoInvalidado) && 'opacity-60 cursor-not-allowed')}>
                {loading ? 'Verificando...' : 'Verificar código'}
              </button>

              {/* Botón Reenviar código con cooldown de 60s (POW-02 §4) */}
              <button
                type="button"
                id="resend-otp-btn"
                onClick={handleResend}
                disabled={resendCooldown > 0 || resending || loading || codigoInvalidado}
                className={clsx(
                  "w-full mt-[10px] py-[7px] px-[14px] text-[0.8rem] font-[600] rounded-full transition-all border",
                  (resendCooldown > 0 || resending || loading || codigoInvalidado)
                    ? "bg-slate-50 text-slate-400 border-slate-200 cursor-not-allowed"
                    : "bg-blue-50 text-blue-600 border-blue-200 hover:bg-blue-100 cursor-pointer"
                )}
              >
                {resending
                  ? 'Enviando nuevo código...'
                  : resendCooldown > 0
                    ? `Reenviar código (${resendCooldown}s)`
                    : 'Reenviar código'}
              </button>
            </div>
          )}

          {/* Footer OTP */}
          {!showForgot && step === '2fa' && (
            <div className="text-center mt-[24px] pt-[20px]" style={{ borderTop: '1px solid #F5F7FA' }}>
              <button
                type="button"
                onClick={() => { clearError(); setOtp(['', '', '', '', '', '']); resetToCredentials() }}
                className="text-[0.82rem] transition-colors hover:text-[#0050F0] bg-transparent border-none cursor-pointer"
                style={{ color: '#94A3B8' }}>
                ← Volver al inicio de sesión
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
